import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import i18n from 'i18next';
import { ChevronRight, X } from 'lucide-react';
import { ORG_AUDIT_GROUPS, type AuditGroup, type OrgAuditLink, type OrgAuditRow } from '@repo/shared-types';
import { auditActionIcon, auditActionLabelKey, formatVnStamp, hasAuditActionLabel, summarizeAudit } from '../../../lib/auditActions';
import { Button } from '@repo/ui/src/components/ui/button';
import { DatePicker } from '../../../components/pickers/DatePicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import { ActivityItem, ActivityList } from '../../../components/activity/ActivityItem';
import { PaginationFooter } from '../../../components/PaginationFooter';
import { useOrgAudit, useOrgAuditActors, type OrgAuditFilters } from '../../../hooks/useAuditLog';

/**
 * Cài đặt › Nhật ký thao tác (THI-259): the org's whole AuditLog, newest
 * first, filtered by group / actor / date. Rows reuse ActivityItem (design 13
 * §5: a list, not a timeline; icons muted, colour is for money only) so the
 * page reads like the student page's "Hoạt động", just for everything.
 *
 * Labels for every action live in the `settings` namespace under
 * `audit.actions`. An action with no label shows its raw name in a mono
 * badge and never crashes: the API already puts it in the `other` group.
 */
const PAGE_SIZE = 50;

const LINK_PATH: Record<OrgAuditLink['type'], string> = {
  invoice: '/invoices',
  student: '/students',
  client: '/clients',
  subject: '/subjects',
};

function AuditRow({ row }: { row: OrgAuditRow }) {
  const { t } = useTranslation('settings');
  const locale = i18n.language === 'vi' ? vi : enUS;
  const labelKey = auditActionLabelKey(row.action);
  const hasLabel = hasAuditActionLabel(row.action);
  const summary = summarizeAudit(row, t);
  const when = new Date(row.createdAt);
  const actorName =
    row.actor.kind === 'member' ? (row.actor.name ?? t('audit.actor.unknownMember'))
    : row.actor.kind === 'platform' ? t('audit.actor.platform')
    : t('audit.actor.system');

  const text: ReactNode = (
    <span className="block min-w-0">
      <span className="block">
        {hasLabel ? (
          <span className="font-medium text-surface-800">{t(labelKey)}</span>
        ) : (
          <span className="rounded-[6px] bg-surface-100 px-[6px] py-[1px] font-mono text-[12px] text-surface-700">{row.action}</span>
        )}
        {summary && <span className="text-surface-600"> · {summary}</span>}
      </span>
      <span className="mt-[2px] block text-xs text-surface-400">
        <span className="tabular-nums">{formatVnStamp(when)}</span>
        {' · '}
        {actorName}
        {row.link && (
          <>
            {' · '}
            <Link to={`${LINK_PATH[row.link.type]}/${row.link.id}`} className="inline-flex items-center gap-[2px] font-medium text-brand-700 hover:text-brand-800">
              {t(`audit.link.${row.link.type}`)}
              <ChevronRight className="h-3 w-3" strokeWidth={2} />
            </Link>
          </>
        )}
      </span>
    </span>
  );

  return (
    <ActivityItem
      icon={auditActionIcon(row.action, row.group)}
      text={text}
      time={formatDistanceToNow(when, { locale, addSuffix: true })}
    />
  );
}

const DEFAULT_FILTERS: OrgAuditFilters = { group: 'all', actor: 'all', from: '', to: '', page: 1, pageSize: PAGE_SIZE };

/** The contextual entry points (student page, Chốt sổ history, the bulk
 *  delete toast) arrive with `?group=money` and the like: read once on mount,
 *  then the page owns its filters. Anything unknown falls back to "all". */
function filtersFromSearch(params: URLSearchParams): OrgAuditFilters {
  const group = params.get('group');
  const day = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '');
  return {
    ...DEFAULT_FILTERS,
    group: group && (ORG_AUDIT_GROUPS as readonly string[]).includes(group) ? (group as AuditGroup) : 'all',
    actor: params.get('actor') || 'all',
    from: day(params.get('from')),
    to: day(params.get('to')),
  };
}

export function AuditLogTab() {
  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<OrgAuditFilters>(() => filtersFromSearch(searchParams));
  const { data, isLoading, isError } = useOrgAudit(filters);
  const { data: actors } = useOrgAuditActors();
  const filtered = filters.group !== 'all' || filters.actor !== 'all' || filters.from !== '' || filters.to !== '';

  /** Every filter change restarts at page 1: page N of the old filter is
   *  usually past the end of the new one. */
  const patch = (p: Partial<OrgAuditFilters>) => setFilters((f) => ({ ...f, ...p, page: 1 }));

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-[16px]">
      <div className="flex flex-wrap items-center gap-[8px]">
        <Select value={filters.group} onValueChange={(v) => patch({ group: v as OrgAuditFilters['group'] })}>
          <SelectTrigger className="w-full sm:w-[190px]" aria-label={t('audit.filters.group')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('audit.groups.all')}</SelectItem>
            {ORG_AUDIT_GROUPS.map((g) => (
              <SelectItem key={g} value={g}>{t(`audit.groups.${g}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.actor} onValueChange={(v) => patch({ actor: v })}>
          <SelectTrigger className="w-full sm:w-[210px]" aria-label={t('audit.filters.actor')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('audit.actor.all')}</SelectItem>
            {(actors?.members ?? []).map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name ?? t('audit.actor.unknownMember')}</SelectItem>
            ))}
            {actors?.hasPlatform && <SelectItem value="platform">{t('audit.actor.platform')}</SelectItem>}
            {actors?.hasSystem && <SelectItem value="system">{t('audit.actor.system')}</SelectItem>}
          </SelectContent>
        </Select>

        {/* Two date inputs and the word between them share the phone's full
            width on a 1fr/auto/1fr grid; `min-w-0` lets a native date input
            shrink below its intrinsic width instead of overflowing the card. */}
        {/* The app's own DatePicker (Vietnamese calendar, same 'YYYY-MM-DD'
            contract as a native input); native date inputs are gone from the
            dashboard, only the time picker is still the OS one. */}
        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-[8px] sm:flex sm:w-auto">
          <DatePicker
            value={filters.from}
            max={filters.to || undefined}
            onChange={(v) => patch({ from: v })}
            clearable
            placeholder={t('audit.filters.from')}
            aria-label={t('audit.filters.from')}
            className="min-w-0 sm:w-[160px]"
          />
          <span className="shrink-0 text-sm text-surface-400">{t('audit.filters.toSep')}</span>
          <DatePicker
            value={filters.to}
            min={filters.from || undefined}
            onChange={(v) => patch({ to: v })}
            clearable
            placeholder={t('audit.filters.to')}
            aria-label={t('audit.filters.to')}
            className="min-w-0 sm:w-[160px]"
          />
        </div>

        {filtered && (
          <Button variant="ghost" size="sm" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-surface-600">
            <X className="mr-[4px] h-3.5 w-3.5" strokeWidth={2} />
            {t('audit.filters.clear')}
          </Button>
        )}
      </div>

      {isError ? (
        <p className="py-[19.2px] text-center text-sm text-destructive">{t('audit.error')}</p>
      ) : isLoading && !data ? (
        <p className="py-[19.2px] text-center text-sm text-surface-400">{t('audit.loading')}</p>
      ) : (
        <>
          <p className="text-xs text-surface-500">
            {total > 0 ? t('audit.count', { count: total }) : null}
          </p>
          <ActivityList count={rows.length} emptyLabel={filtered ? t('audit.emptyFiltered') : t('audit.empty')}>
            {rows.map((row) => (
              <AuditRow key={row.id} row={row} />
            ))}
          </ActivityList>
          <PaginationFooter
            page={filters.page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
            summary={t('audit.pager.summary', { page: filters.page, pages: Math.max(1, Math.ceil(total / PAGE_SIZE)) })}
            prevLabel={t('audit.pager.prev')}
            nextLabel={t('audit.pager.next')}
          />
        </>
      )}
    </div>
  );
}
